import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/site/SiteBits";
import { usePageMeta, useLive } from "@/lib/site";
import { stageApi, compact, OPEN_TO_LABEL } from "@/lib/stageApi";
import { CreatorAvatar, VerifiedMark, AiTag } from "@/components/stage/PostCard";

const SORTS = [
  { key: "relevance", label: "Relevance" },
  { key: "followers", label: "Most followed" },
  { key: "newest", label: "Newest" },
];

export function CreatorCard({ c }) {
  return (
    <Link to={`/@${c.handle}`} className="group block overflow-hidden rounded-2xl border border-border bg-card card-hover" data-testid={`creator-${c.handle}`}>
      <div className="relative h-24 bg-muted">
        {c.banner_url && <img src={c.banner_url} alt="" loading="lazy" className="h-full w-full object-cover" />}
        <div className="absolute -bottom-7 left-4 rounded-full border-4 border-card"><CreatorAvatar author={c} size={56} /></div>
      </div>
      <div className="px-4 pb-4 pt-9">
        <div className="flex items-center gap-1.5">
          <h3 className="truncate font-semibold">{c.display_name || c.handle}</h3>
          <VerifiedMark author={c} />
          <AiTag author={c} />
        </div>
        <p className="text-xs text-muted-foreground">@{c.handle}{c.role_label ? ` · ${c.role_label}` : ""}</p>
        {c.bio && <p className="mt-2 line-clamp-2 text-sm text-foreground/80">{c.bio}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><Users size={12} /> <span className="font-mono-num font-semibold text-foreground">{compact(c.stats?.followers)}</span> followers</span>
          {c.location && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {c.location}</span>}
        </div>
        {Array.isArray(c.categories) && c.categories.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {c.categories.slice(0, 3).map((k) => <span key={k} className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium capitalize">{k}</span>)}
            {(c.open_to || []).filter((k) => k !== "not_open").slice(0, 2).map((k) => (
              <span key={k} className="rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ background: "#FFE1F0", color: "var(--fable)" }}>Open to {OPEN_TO_LABEL[k] || k}</span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

export default function Creators() {
  usePageMeta("Creators on Fable +", "Browse the creators, hosts and pros on Fable + Stage by category, location and what they're open to — collabs, podcast guests, brand deals and more.");
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const openTo = params.get("open_to") || "";
  const sort = params.get("sort") || "relevance";
  const [search, setSearch] = useState(q);
  const config = useLive(() => stageApi.config(), []);
  const [data, setData] = useState(undefined);

  useEffect(() => {
    let alive = true;
    setData(undefined);
    stageApi
      .creators({ q: q || undefined, category: category || undefined, open_to: openTo || undefined, sort, limit: 30 })
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, [q, category, openTo, sort]);

  const set = (patch) => {
    const n = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    setParams(n, { replace: true });
  };
  const categories = useMemo(() => config?.categories || [], [config]);
  const openOptions = useMemo(() => (config?.open_to || []).filter((o) => o.key !== "not_open"), [config]);
  const items = data?.items || [];

  return (
    <div>
      <PageHeader kicker="Stage" title="The people behind the bookings." lede="Creators, hosts, teachers and pros you can follow on Stage and book in the marketplace, all on one account.">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            set({ q: search.trim() });
          }}
          className="flex max-w-lg items-center gap-2"
          role="search"
        >
          <label className="sr-only" htmlFor="creators-q">Search creators</label>
          <div className="relative flex-1">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input id="creators-q" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, handle or what they do" data-testid="creators-search" className="w-full rounded-full border border-border bg-card py-2.5 pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </div>
          <Button type="submit" className="rounded-full px-5">Search</Button>
        </form>
      </PageHeader>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <button type="button" onClick={() => set({ category: "" })} aria-pressed={!category} className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium ${!category ? "border-transparent bg-foreground text-background" : "border-border bg-card hover:bg-muted"}`}>All</button>
            {categories.map((c) => (
              <button key={c.key} type="button" onClick={() => set({ category: category === c.key ? "" : c.key })} aria-pressed={category === c.key} data-testid={`creators-cat-${c.key}`} className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium ${category === c.key ? "border-transparent bg-foreground text-background" : "border-border bg-card hover:bg-muted"}`}>{c.label}</button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <label className="sr-only" htmlFor="creators-open">Open to</label>
            <select id="creators-open" value={openTo} onChange={(e) => set({ open_to: e.target.value })} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm">
              <option value="">Open to anything</option>
              {openOptions.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            <label className="sr-only" htmlFor="creators-sort">Sort</label>
            <select id="creators-sort" value={sort} onChange={(e) => set({ sort: e.target.value })} className="rounded-full border border-border bg-card px-3 py-1.5 text-sm" data-testid="creators-sort">
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {data === undefined ? (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}</div>
        ) : data === null ? (
          <p className="mt-10 text-center text-muted-foreground">Couldn't load creators right now. Please try again shortly.</p>
        ) : items.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-border p-10 text-center">
            <p className="font-semibold">No creators match that yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Try a broader search or clear the filters.</p>
            <Button variant="outline" className="mt-4 rounded-full" onClick={() => { setSearch(""); setParams({}, { replace: true }); }}>Clear filters</Button>
          </div>
        ) : (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="creators-grid">
            {items.map((c) => <CreatorCard key={c.id} c={c} />)}
          </div>
        )}
      </div>
    </div>
  );
}
